import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Search, 
  Plus, 
  Grid, 
  List, 
  Filter, 
  Download, 
  Trash, 
  Link, 
  Edit, 
  MoreHorizontal,
  X,
  Check,
  Image,
  Video,
  FileText,
  CheckSquare,
  User
} from 'lucide-react';
import { format } from 'date-fns';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { MediaDisplay } from '../components/posts/previews/MediaDisplay';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { PageLayout } from '../components/layout';
import { SearchBar } from '../components/common/SearchBar';
import { MediaTypeDropdown, MediaTypeOption } from '../components/common/MediaTypeDropdown';
import { SortDropdown, SortOption } from '../components/common/SortDropdown';
import { useMediaApi } from '../hooks/useMediaApi';
import { MediaFile } from '../hooks/useMediaGallery';
import type { Brand } from '../types';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useMediaGalleryUrlState } from '../store/mediaGalleryStore';
import { useBrands } from '../hooks/useBrands';

// Get API URL from environment variables
const API_URL = import.meta.env.VITE_API_URL || 'https://api.socialness.ai';

// Remove mock data, we'll use real data from the API

// Media type options for filter
const mediaTypeOptions = [
  { id: 'all', label: 'mediaGallery.filters.all', icon: null },
  { id: 'image', label: 'mediaGallery.filters.images', icon: Image },
  { id: 'video', label: 'mediaGallery.filters.videos', icon: Video },
  { id: 'document', label: 'mediaGallery.filters.documents', icon: FileText }
];

// Sort options
const sortOptions = [
  { id: 'newest', label: 'mediaGallery.sort.newest' },
  { id: 'oldest', label: 'mediaGallery.sort.oldest' },
  { id: 'nameAZ', label: 'mediaGallery.sort.nameAZ' },
  { id: 'nameZA', label: 'mediaGallery.sort.nameZA' },
  { id: 'size', label: 'mediaGallery.sort.size' }
];

interface MediaItemProps {
  item: MediaFile;
  isSelected: boolean;
  onSelect: (key: string) => void;
  onDelete: (key: string) => void;
  onDownload: (item: MediaFile) => void;
  onCopyUrl: (url: string) => void;
  onRename: (key: string, newName: string) => void;
  viewType: 'grid' | 'list';
  isSelectionMode: boolean;
}

// Individual media item component
const MediaItem: React.FC<MediaItemProps> = ({ 
  item, 
  isSelected, 
  onSelect, 
  onDelete, 
  onDownload, 
  onCopyUrl, 
  onRename,
  viewType,
  isSelectionMode
}) => {
  const { t } = useTranslation();
  const [showActions, setShowActions] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const { user } = useAuthStore();
  
  const isProfilePicture = user?.photoURL && item.url === user.photoURL;

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(item.url);
    onCopyUrl(item.url);
  };
  
  const getIcon = () => {
    switch(item.mediaType) {
      case 'image': return <Image className="w-5 h-5 sm:w-6 sm:h-6" />;
      case 'video': return <Video className="w-5 h-5 sm:w-6 sm:h-6" />;
      case 'pdf': 
      case 'document': return <FileText className="w-5 h-5 sm:w-6 sm:h-6" />;
      default: return <FileText className="w-5 h-5 sm:w-6 sm:h-6" />;
    }
  };
  
  // Extract filename from key or use name from API
  const getFileName = () => {
    // Use name from API if available, otherwise extract from key
    if (item.name) {
      return item.name;
    }
    const keyParts = item.key.split('/');
    return keyParts[keyParts.length - 1];
  };
  
  // Helper to determine aspect ratio based on mediaType and filename
  const getAspectRatio = () => {
    // Check for aspect ratio hints in the filename
    const fileName = getFileName().toLowerCase();
    
    // Look for common aspect ratio indicators in filename
    if (fileName.includes('9x16') || fileName.includes('9-16') || fileName.includes('vertical') || fileName.includes('reel') || fileName.includes('story')) {
      return '9/16'; // Vertical format (Reels, Stories, TikTok)
    }
    
    if (fileName.includes('4x5') || fileName.includes('4-5') || fileName.includes('portrait')) {
      return '4/5';  // Instagram portrait format
    }
    
    if (fileName.includes('1x1') || fileName.includes('1-1') || fileName.includes('square')) {
      return '1/1';  // Square format
    }
    
    if (fileName.includes('16x9') || fileName.includes('16-9') || fileName.includes('landscape')) {
      return '16/9'; // Landscape format
    }
    
    // Default aspect ratios based on media type if no hints found
    if (item.mediaType === 'video') {
      // Most videos from social platforms are vertical (9:16) these days
      // Check size - if height > width, it's likely vertical
      if (item.key.includes('reel') || item.key.includes('tiktok') || 
          item.key.includes('story') || item.key.includes('vertical')) {
        return '9/16';
      }
      return '16/9';  // Default for videos is landscape
    }
    
    // For images, default to their natural format
    return '1/1';   // Default square for images
  };
  
  // Handle item click based on mode
  const handleItemClick = () => {
    if (isSelectionMode) {
      onSelect(item.key);
    } else {
      setShowPreview(true);
    }
  };
  
  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  if (viewType === 'grid') {
    const aspectRatioClass = (() => {
      const ratio = getAspectRatio();
      if (ratio === '9/16') return 'aspect-[9/16]';
      if (ratio === '4/5') return 'aspect-[4/5]';
      if (ratio === '1/1') return 'aspect-square';
      if (ratio === '16/9') return 'aspect-video';
      return 'aspect-square'; // Default
    })();

    return (
      <>
        <div 
          className={`group relative overflow-hidden rounded-lg cursor-pointer ${aspectRatioClass} h-full w-full min-h-[150px] sm:min-h-[180px]`}
          onClick={handleItemClick}
          onMouseEnter={() => setShowActions(true)}
          onMouseLeave={() => setShowActions(false)}
          onTouchStart={() => setShowActions(true)}
        >
          {/* Profile Picture Badge */}
          {isProfilePicture && !isSelectionMode && (
            <div
              className="absolute top-2 left-2 z-20 p-1 bg-primary-500 text-white rounded-full shadow"
              title={t('mediaGallery.item.profilePictureBadge', 'Current Profile Picture')}
            >
              <User className="w-3 h-3" />
            </div>
          )}

          {/* Selection indicator - only show when in selection mode */}
          {isSelectionMode && (
            <div className={`absolute top-2 right-2 z-20 w-5 h-5 rounded-full ${isSelected ? 'bg-blue-500 border-blue-500' : 'bg-black/30 hover:bg-black/50'} flex items-center justify-center transition-all duration-150`}>
              {isSelected && <Check className="w-3 h-3 text-white" />}
            </div>
          )}
          
          {/* Media content */}
          {item.mediaType === 'image' || item.mediaType === 'video' ? (
            <div className="w-full h-full">
              <MediaDisplay
                mediaUrl={item.url}
                altText={getFileName()}
                type={item.mediaType as 'image' | 'video'}
                objectFit="cover"
                aspectRatio={getAspectRatio()}
                className="rounded-lg w-full h-full object-center"
              />
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100">
              {getIcon()}
            </div>
          )}
          
          {/* Hover overlay with actions - visible on mobile touch */}
          <div className={`absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center ${showActions ? 'opacity-100' : 'opacity-0 sm:group-hover:opacity-100'} transition-opacity duration-200`}>
            <div className="flex gap-2">
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onDownload(item);
                }}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                title={t('mediaGallery.item.download')}
              >
                <Download className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  handleCopyUrl();
                }}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                title={t('mediaGallery.item.copyUrl')}
              >
                <Link className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
              <button 
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(item.key);
                }}
                className="p-2 rounded-full bg-white/20 hover:bg-white/30 text-white transition-colors"
                title={t('mediaGallery.item.delete')}
              >
                <Trash className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </div>
        </div>
        
        {/* Preview modal */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-3" onClick={() => setShowPreview(false)}>
            <div className="relative max-w-4xl max-h-[90vh] w-full bg-white rounded-lg overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="absolute top-2 right-2 z-10">
                <button 
                  className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              
              <div className="p-2 max-h-[80vh] overflow-auto">
                {item.mediaType === 'image' ? (
                  <img 
                    src={item.url} 
                    alt={getFileName()} 
                    className="max-w-full max-h-[70vh] object-contain mx-auto"
                    style={{ aspectRatio: getAspectRatio() }}
                  />
                ) : item.mediaType === 'video' ? (
                  <video 
                    src={item.url}
                    controls
                    autoPlay
                    className="max-w-full max-h-[70vh] mx-auto"
                    style={{ aspectRatio: getAspectRatio() }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 sm:p-12">
                    {getIcon()}
                    <p className="mt-4 text-gray-700 text-center break-all">{getFileName()}</p>
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md"
                    >
                      {t('mediaGallery.item.download')}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="border-t p-3 sm:p-4">
                <h3 className="text-base sm:text-lg font-semibold break-all">{getFileName()}</h3>
                <div className="flex items-center mt-2 text-xs sm:text-sm text-gray-500">
                  <span>{formatFileSize(item.size)}</span>
                  <span className="mx-2">•</span>
                  <span>{format(new Date(item.lastModified), 'MMM d, yyyy')}</span>
                </div>
                
                <div className="flex flex-wrap mt-3 sm:mt-4 gap-2 sm:gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(item);
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.download')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyUrl();
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Link className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.copyUrl')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item.key);
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    <Trash className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  } else {
    // List view
    const listItemContent = (
      <>
        {isProfilePicture && (
          <div
            className="mr-2 flex-shrink-0 p-0.5 bg-primary-500 text-white rounded-full shadow"
            title={t('mediaGallery.item.profilePictureBadge', 'Current Profile Picture')}
          >
            <User className="w-3 h-3" />
          </div>
        )}
        <div className="flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 bg-gray-100 rounded-md overflow-hidden flex items-center justify-center text-gray-500 mr-3 sm:mr-4">
          {item.mediaType === 'image' || item.mediaType === 'video' ? (
            <MediaDisplay
              mediaUrl={item.url}
              altText={getFileName()}
              type={item.mediaType as 'image' | 'video'}
              objectFit="cover"
              className="w-full h-full"
            />
          ) : (
            getIcon()
          )}
        </div>
      </>
    );

    return (
      <>
        <div 
          className={`relative flex items-center p-2 sm:p-3 border-b ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} cursor-pointer transition-colors duration-150`}
          onClick={handleItemClick}
        >
          {/* Checkbox and Profile Badge (if applicable) */}
          <div className="flex items-center">
            {isSelectionMode && (
              <div 
                className={`w-5 h-5 rounded-md border ${isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-300 bg-white'} flex items-center justify-center mr-3 cursor-pointer`}
                onClick={(e) => { e.stopPropagation(); onSelect(item.key); }}
              >
                {isSelected && <Check className="w-3 h-3 text-white" />}
              </div>
            )}
            {listItemContent}
          </div>
          
          <div className="flex-1 min-w-0 mr-2">
            <h3 className="font-medium text-xs sm:text-sm truncate">{getFileName()}</h3>
            <div className="mt-0.5 sm:mt-1 flex items-center text-xs text-gray-500">
              <span>{formatFileSize(item.size)}</span>
              <span className="mx-1">•</span>
              <span>{format(new Date(item.lastModified), 'MMM d, yyyy')}</span>
            </div>
          </div>
          
          <div className="flex items-center">
            <button 
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                onDownload(item);
              }}
              title={t('mediaGallery.item.download')}
            >
              <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <button 
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                handleCopyUrl();
              }}
              title={t('mediaGallery.item.copyUrl')}
            >
              <Link className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <button 
              className="p-1.5 sm:p-2 rounded-full text-gray-300 cursor-not-allowed"
              title={t('mediaGallery.item.renameDisabled', 'Rename functionality coming soon')}
            >
              <Edit className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
            <button 
              className="p-1.5 sm:p-2 rounded-full hover:bg-gray-100 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(item.key);
              }}
              title={t('mediaGallery.item.delete')}
            >
              <Trash className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
        </div>
        
        {/* Preview modal (same for both views) */}
        {showPreview && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-3" onClick={() => setShowPreview(false)}>
            <div className="relative max-w-4xl max-h-[90vh] w-full bg-white rounded-lg overflow-hidden shadow-xl" onClick={(e) => e.stopPropagation()}>
              <div className="absolute top-2 right-2 z-10">
                <button 
                  className="p-1 rounded-full bg-black/50 text-white hover:bg-black/70 transition-colors"
                  onClick={() => setShowPreview(false)}
                >
                  <X className="w-5 h-5 sm:w-6 sm:h-6" />
                </button>
              </div>
              
              <div className="p-2 max-h-[80vh] overflow-auto">
                {item.mediaType === 'image' ? (
                  <img 
                    src={item.url} 
                    alt={getFileName()} 
                    className="max-w-full max-h-[70vh] object-contain mx-auto"
                    style={{ aspectRatio: getAspectRatio() }}
                  />
                ) : item.mediaType === 'video' ? (
                  <video 
                    src={item.url}
                    controls
                    autoPlay
                    className="max-w-full max-h-[70vh] mx-auto"
                    style={{ aspectRatio: getAspectRatio() }}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center p-6 sm:p-12">
                    {getIcon()}
                    <p className="mt-4 text-gray-700 text-center break-all">{getFileName()}</p>
                    <a 
                      href={item.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-md"
                    >
                      {t('mediaGallery.item.download')}
                    </a>
                  </div>
                )}
              </div>
              
              <div className="border-t p-3 sm:p-4">
                <h3 className="text-base sm:text-lg font-semibold break-all">{getFileName()}</h3>
                <div className="flex items-center mt-2 text-xs sm:text-sm text-gray-500">
                  <span>{formatFileSize(item.size)}</span>
                  <span className="mx-2">•</span>
                  <span>{format(new Date(item.lastModified), 'MMM d, yyyy')}</span>
                </div>
                
                <div className="flex flex-wrap mt-3 sm:mt-4 gap-2 sm:gap-3">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(item);
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Download className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.download')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopyUrl();
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    <Link className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.copyUrl')}
                  </button>
                  <button
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-gray-100 border border-gray-200 rounded-md text-gray-400 cursor-not-allowed"
                  >
                    <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.rename')}
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(item.key);
                    }}
                    className="flex items-center px-2 sm:px-3 py-1.5 text-xs sm:text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  >
                    <Trash className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5" />
                    {t('mediaGallery.item.delete')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
  }
};

interface RenameModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (newName: string) => void;
  currentName: string;
}

const RenameModal: React.FC<RenameModalProps> = ({ isOpen, onClose, onConfirm, currentName }) => {
  const { t } = useTranslation();
  const [newName, setNewName] = useState(currentName);

  useEffect(() => {
    setNewName(currentName);
  }, [currentName]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (newName.trim()) {
      onConfirm(newName.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('mediaGallery.rename.title', 'Rename File')}
        </h3>
        
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
              {t('mediaGallery.rename.fileNameLabel', 'File Name')}
            </label>
            <input
              type="text"
              id="fileName"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              autoFocus
            />
          </div>
          
          <div className="flex justify-end space-x-3">
            <button
              type="button"
              className="px-4 py-2 border rounded-md hover:bg-gray-50"
              onClick={onClose}
            >
              {t('mediaGallery.rename.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              disabled={!newName.trim() || newName.trim() === currentName}
            >
              {t('mediaGallery.rename.confirm', 'Rename')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MediaGallery: React.FC = () => {
  const { t } = useTranslation();
  const { 
    viewType, setViewType,
    selectedBrandId, setSelectedBrandId,
    selectedMediaType, setSelectedMediaType,
    sortOption, setSortOption,
    searchQuery, setSearchQuery
  } = useMediaGalleryUrlState();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteSuccess, setBulkDeleteSuccess] = useState(false);
  
  // Add state for rename functionality
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [itemToRename, setItemToRename] = useState<{ key: string; currentName: string } | null>(null);
  
  // Use the MediaAPI hook
  const mediaApi = useMediaApi();
  const { data, isLoading, error, refetch } = mediaApi.useMediaQuery();
  const queryClient = useQueryClient();
  
  // Fetch brands data
  const { data: brandsData, isLoading: brandsLoading } = useBrands();
  const brands: Brand[] = brandsData ? (brandsData as any).brands?.map((b: any) => ({
    id: b.id, 
    name: b.name, 
    logo: b.logo
  })) || [] : [];
  
  // Set document title
  useDocumentTitle(t('mediaGallery.title'));
  
  // Process files from the API response
  const mediaFiles = data?.files || [];
  
  // Filter brands to only show those with media files
  const brandsWithMedia = useMemo(() => {
    if (!mediaFiles.length) return brands; // If no media, show all brands for upload
    
    // Get unique brand IDs from media files (assuming media files have brandId property)
    // Note: We need to check if MediaFile actually has brandId property
    // For now, we'll show all brands since the media structure might not include brandId
    return brands;
  }, [brands, mediaFiles]);
  
  // Setup allBrandsOption for the brand dropdown
  const allBrandsOption: Brand = { id: 'all', name: t('mediaGallery.filters.allBrands'), logo: '/logo.svg' };
  const brandsForDropdown = [allBrandsOption, ...brandsWithMedia];
  
  // Clear copied URL notification after 2 seconds
  useEffect(() => {
    if (copiedUrl) {
      const timer = setTimeout(() => {
        setCopiedUrl(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedUrl]);
  
  // Filter and sort media items
  const filteredMedia = mediaFiles.filter(item => {
    // Extract filename from key for search
    const fileName = item.key.split('/').pop() || '';
    
    // Filter by search query
    const matchesSearch = !searchQuery || 
      fileName.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Filter by media type
    const matchesType = selectedMediaType === 'all' || 
      item.mediaType.toLowerCase() === selectedMediaType.toLowerCase();
    
    return matchesSearch && matchesType;
  }).sort((a, b) => {
    // Sort by selected option
    switch(sortOption) {
      case 'newest':
        return new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime();
      case 'oldest':
        return new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime();
      case 'nameAZ': {
        const nameA = a.key.split('/').pop() || '';
        const nameB = b.key.split('/').pop() || '';
        return nameA.localeCompare(nameB);
      }
      case 'nameZA': {
        const nameA = a.key.split('/').pop() || '';
        const nameB = b.key.split('/').pop() || '';
        return nameB.localeCompare(nameA);
      }
      case 'size':
        return b.size - a.size;
      default:
        return 0;
    }
  });
  
  const toggleItemSelection = (itemKey: string) => {
    if (isSelectionMode) {
      // In selection mode, toggle selection
      if (selectedItems.includes(itemKey)) {
        setSelectedItems(selectedItems.filter(key => key !== itemKey));
      } else {
        setSelectedItems([...selectedItems, itemKey]);
      }
    }
    // No behavior when not in selection mode (preview will be handled in MediaItem)
  };
  
  // Toggle selection mode
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    // Clear selections when exiting selection mode
    if (isSelectionMode) {
      setSelectedItems([]);
    }
  };
  
  const handleDelete = (itemKey: string) => {
    setItemToDelete(itemKey);
    setShowDeleteConfirmation(true);
  };
  
  const confirmDelete = async () => {
    if (!itemToDelete) return;
    
    try {
      // Show loading animation
      setIsBulkDeleting(true);
      setShowDeleteConfirmation(false);
      
      console.log('Deleting single file:', itemToDelete);
      
      // Make the API call
      await mediaApi.deleteMediaAsync({ key: itemToDelete });
      
      // Clear the item to delete
      setItemToDelete(null);
      
      // Explicitly refetch the media data to update the UI and wait for it to complete
      console.log('Refetching media data after single delete...');
      await refetch();
      
      // Show success notification AFTER data is refreshed
      setBulkDeleteSuccess(true);
      
      // Hide success notification after 3 seconds
      setTimeout(() => {
        setBulkDeleteSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error deleting file:', error);
      // Handle error - perhaps show a notification
    } finally {
      setIsBulkDeleting(false);
    }
  };
  
  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) return;
    
    try {
      console.log('Bulk deleting files:', selectedItems);
      
      // Show loading animation
      setIsBulkDeleting(true);
      setShowDeleteConfirmation(false);
      
      // Make sure we're using the full file keys as shown in the example
      const result = await mediaApi.bulkDeleteMediaAsync({ fileNames: selectedItems });
      
      console.log('Bulk delete operation completed:', result);
      
      // Clear selected items after successful deletion
      setSelectedItems([]);
      
      // Explicitly refetch the media data to update the UI and WAIT for it to complete
      console.log('Refetching media data after bulk delete...');
      await refetch();
      
      // Force React Query to update UI from fresh data
      queryClient.removeQueries({ queryKey: ['media'] });
      
      // Only show success notification after API confirms deletion and data is refetched
      setBulkDeleteSuccess(true);
      
      // Hide success notification after 3 seconds
      setTimeout(() => {
        setBulkDeleteSuccess(false);
      }, 3000);
    } catch (error) {
      console.error('Error bulk deleting files:', error);
      // Handle error - perhaps show a notification
    } finally {
      setIsBulkDeleting(false);
    }
  };
  
  const handleDownload = (item: MediaFile) => {
    mediaApi.downloadMedia(item);
  };
  
  const handleCopyUrl = (url: string) => {
    setCopiedUrl(url);
  };
  
  const handleFileUpload = async (file: File) => {
    try {
      setIsUploading(true);
      setUploadError(null);
      setUploadSuccess(false);
      await mediaApi.uploadMedia({ file });
      setShowUploadModal(false);
      setUploadSuccess(true);
      // The query will be automatically refetched due to the invalidation in the hook
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadError((error as Error).message);
      // Handle error - perhaps show a notification
    } finally {
      setIsUploading(false);
    }
  };
  
  // Add the rename function
  const handleRename = (key: string, currentName: string) => {
    setItemToRename({ key, currentName });
    setShowRenameModal(true);
  };
  
  const confirmRename = async (newName: string) => {
    if (!itemToRename) return;
    
    try {
      // Call your API to rename the file
      // For now, we'll just close the modal and refresh
      setShowRenameModal(false);
      
      // In a real implementation, you would call an API like:
      // await mediaApi.renameMedia({ key: itemToRename.key, newName });
      
      // For now, just show a success message
      alert(`File would be renamed from "${itemToRename.currentName}" to "${newName}"`);
      
      // Reset state
      setItemToRename(null);
      
      // In a real implementation, you'd invalidate queries to refresh the data
      // queryClient.invalidateQueries({ queryKey: ['media'] });
    } catch (error) {
      console.error('Error renaming file:', error);
      alert('Failed to rename file. Please try again.');
    }
  };
  
  return (
    <>
      <PageLayout
        title={t('mediaGallery.title')}
        subtitle={t('mediaGallery.subtitle')}
        actions={
          <div className="flex flex-wrap w-full sm:w-auto items-center gap-2 sm:gap-3">
            <button
              className={`flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 ${isSelectionMode 
                ? 'bg-primary-600 text-white' 
                : 'bg-white text-primary-600 border border-primary-600'} rounded-lg hover:bg-primary-700 hover:text-white transition-colors`}
              onClick={toggleSelectionMode}
            >
              <CheckSquare className="w-5 h-5 mr-2" />
              <span className="whitespace-nowrap">
                {isSelectionMode ? t('mediaGallery.exitSelection') : t('mediaGallery.selectMultiple')}
              </span>
            </button>
            
            <button
              className="flex-1 sm:flex-none inline-flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
              onClick={() => setShowUploadModal(true)}
            >
              <Plus className="w-5 h-5 mr-2" />
              <span className="whitespace-nowrap">{t('mediaGallery.upload')}</span>
            </button>
          </div>
        }
      >
          
          {/* Display selected count when in selection mode */}
          {isSelectionMode && selectedItems.length > 0 && (
            <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between bg-primary-50 p-3 rounded-lg">
              <div className="flex items-center mb-3 sm:mb-0">
                <CheckSquare className="w-5 h-5 text-primary-600 mr-2" />
                <span className="text-sm font-medium">
                  {t('mediaGallery.selectedCount', { count: selectedItems.length })}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  onClick={() => setSelectedItems([])}
                >
                  {t('mediaGallery.clearSelection')}
                </button>
                {selectedItems.length === 1 && (
                  <button
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={() => {
                      const item = mediaFiles.find(file => file.key === selectedItems[0]);
                      if (item) handleCopyUrl(item.url);
                    }}
                  >
                    <Link className="w-4 h-4 mr-1 inline-block" />
                    {t('mediaGallery.item.copyUrl')}
                  </button>
                )}
                {selectedItems.length === 1 && (
                  <button
                    className="px-3 py-1.5 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                    onClick={() => {
                      const item = mediaFiles.find(file => file.key === selectedItems[0]);
                      if (item) handleDownload(item);
                    }}
                  >
                    <Download className="w-4 h-4 mr-1 inline-block" />
                    {t('mediaGallery.item.download')}
                  </button>
                )}
                <button
                  className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700"
                  onClick={() => {
                    if (selectedItems.length === 1) {
                      handleDelete(selectedItems[0]);
                    } else if (selectedItems.length > 1) {
                      // Show confirmation for bulk delete
                      setItemToDelete(null); // Clear single item delete
                      setShowDeleteConfirmation(true);
                    }
                  }}
                >
                  <Trash className="w-4 h-4 mr-1 inline-block" />
                  {selectedItems.length > 1 
                    ? t('mediaGallery.bulkDelete', { count: selectedItems.length }) 
                    : t('mediaGallery.item.delete')}
                </button>
              </div>
            </div>
          )}
          
          {/* Total media count just under header actions */}
          <div className="mt-2 text-sm text-gray-500 text-right">
            {t('mediaGallery.totalMedia', { count: mediaFiles.length })}
          </div>

          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:gap-3">
              <SearchBar 
                value={searchQuery}
                onChange={setSearchQuery}
                placeholder={t('mediaGallery.search')}
                className="w-full sm:w-auto sm:flex-1"
              />
              
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <div className="flex items-center space-x-2 border rounded-md p-1 bg-white">
                  <button
                    className={`p-1.5 rounded-md ${viewType === 'grid' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    onClick={() => setViewType('grid')}
                    title={t('mediaGallery.view.grid')}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    className={`p-1.5 rounded-md ${viewType === 'list' ? 'bg-primary-50 text-primary-700' : 'text-gray-500 hover:bg-gray-100'}`}
                    onClick={() => setViewType('list')}
                    title={t('mediaGallery.view.list')}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
                
                <BrandDropdown
                  brands={brandsForDropdown}
                  selectedBrandId={selectedBrandId}
                  onBrandSelect={setSelectedBrandId}
                  showRedirectionCard={false}
                  className="min-w-[120px] sm:min-w-[140px]"
                />
                
                <MediaTypeDropdown 
                  options={mediaTypeOptions}
                  selectedTypeId={selectedMediaType}
                  onTypeSelect={setSelectedMediaType}
                  className="min-w-[120px] sm:min-w-[140px]"
                />
                
                <SortDropdown
                  options={sortOptions}
                  selectedOptionId={sortOption}
                  onOptionSelect={setSortOption}
                  className="min-w-[120px] sm:min-w-[140px]"
                />
              </div>
            </div>
          </div>
          
          {/* Media items grid/list */}
          {isLoading ? (
            // Skeleton Loader for Grid View
            <div className="animate-pulse">
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3 sm:gap-4 pb-8 grid-flow-dense">
                {[...Array(12)].map((_, index) => (
                  <div key={index} className="aspect-square bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="py-8 sm:py-12 flex flex-col items-center justify-center text-center">
              <div className="text-red-500 text-lg mb-2">
                {t('mediaGallery.error')}
              </div>
              <p className="text-gray-600">{(error as Error).message}</p>
            </div>
          ) : filteredMedia.length > 0 ? (
            viewType === 'grid' ? (
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3 sm:gap-4 pb-8 grid-flow-dense">
                {filteredMedia.map((item, index) => {
                  // Get aspect ratio reliably using the proper method
                  const determineGridSpan = () => {
                    // Extract filename from item.key
                    const getFileName = () => {
                      const keyParts = item.key.split('/');
                      return keyParts[keyParts.length - 1];
                    };
                    
                    // Define our own aspect ratio logic (similar to what's in MediaItem)
                    const getItemAspectRatio = () => {
                      const fileName = getFileName().toLowerCase();
                      
                      // Look for common aspect ratio indicators in filename
                      if (fileName.includes('9x16') || fileName.includes('9-16') || fileName.includes('vertical') || fileName.includes('reel') || fileName.includes('story')) {
                        return '9/16'; // Vertical format (Reels, Stories, TikTok)
                      }
                      
                      if (fileName.includes('4x5') || fileName.includes('4-5') || fileName.includes('portrait')) {
                        return '4/5';  // Instagram portrait format
                      }
                      
                      if (fileName.includes('1x1') || fileName.includes('1-1') || fileName.includes('square')) {
                        return '1/1';  // Square format
                      }
                      
                      if (fileName.includes('16x9') || fileName.includes('16-9') || fileName.includes('landscape')) {
                        return '16/9'; // Landscape format
                      }
                      
                      // Default aspect ratios based on media type if no hints found
                      if (item.mediaType === 'video') {
                        if (item.key.includes('reel') || item.key.includes('tiktok') || 
                            item.key.includes('story') || item.key.includes('vertical')) {
                          return '9/16';
                        }
                        return '16/9';  // Default for videos is landscape
                      }
                      
                      // For images, default to their natural format
                      return '1/1';   // Default square for images
                    };
                    
                    // Get the aspect ratio for this item
                    const aspectRatio = getItemAspectRatio();
                    
                    // Determine grid span based on aspect ratio and position
                    const gridSpan = {
                      classes: '',
                      aspectClass: ''
                    };
                    
                    // Set aspect ratio class
                    if (aspectRatio === '9/16') {
                      gridSpan.aspectClass = 'aspect-[9/16]';
                    } else if (aspectRatio === '4/5') {
                      gridSpan.aspectClass = 'aspect-[4/5]';
                    } else if (aspectRatio === '16/9') {
                      gridSpan.aspectClass = 'aspect-video';
                    } else {
                      gridSpan.aspectClass = 'aspect-square';
                    }
                    
                    // Pattern based on position - use a more distributed approach for wide screens
                    if (index % 24 === 0 || index % 24 === 13) { // Feature item (2x2) - one near start, one near middle
                      gridSpan.classes = 'col-span-2 row-span-2';
                    } else if (index % 24 === 4 || index % 24 === 18) { // Tall vertical items
                      gridSpan.classes = 'row-span-2';
                    } else if (index % 24 === 8 || index % 24 === 20) { // Wide horizontal items
                      gridSpan.classes = 'col-span-2';
                    } else if (index % 24 === 11 || index % 24 === 22) { // Large portrait items
                      gridSpan.classes = 'col-span-2 row-span-2';
                    }
                    
                    return gridSpan;
                  };
                  
                  const gridSpan = determineGridSpan();
                  
                  return (
                    <div 
                      key={item.key} 
                      className={`${gridSpan.classes} h-full`}
                    >
                      <div className={`h-full w-full ${gridSpan.aspectClass} overflow-hidden`}>
                        <MediaItem
                          item={item}
                          isSelected={selectedItems.includes(item.key)}
                          onSelect={toggleItemSelection}
                          onDelete={handleDelete}
                          onDownload={handleDownload}
                          onCopyUrl={handleCopyUrl}
                          onRename={handleRename}
                          viewType={viewType}
                          isSelectionMode={isSelectionMode}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="divide-y border-t border-b overflow-x-auto">
                {filteredMedia.map(item => (
                  <MediaItem
                    key={item.key}
                    item={item}
                    isSelected={selectedItems.includes(item.key)}
                    onSelect={toggleItemSelection}
                    onDelete={handleDelete}
                    onDownload={handleDownload}
                    onCopyUrl={handleCopyUrl}
                    onRename={handleRename}
                    viewType={viewType}
                    isSelectionMode={isSelectionMode}
                  />
                ))}
              </div>
            )
          ) : (
            <div className="py-8 sm:py-12 flex flex-col items-center justify-center text-center">
              <Image className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">{t('mediaGallery.noResults')}</h3>
              {searchQuery && <p className="text-sm text-gray-500">{t('mediaGallery.filters.all')}</p>}
            </div>
          )}
      </PageLayout>
      
      {/* Toast notification for copied URL */}
      {copiedUrl && (
        <div className="fixed bottom-4 right-4 bg-gray-800 text-white px-4 py-2 rounded-md shadow-lg flex items-center space-x-2 z-50">
          <Check className="w-5 h-5" />
          <span>{t('mediaGallery.item.urlCopied')}</span>
        </div>
      )}
      
      {/* Delete confirmation modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {itemToDelete 
                ? t('mediaGallery.deleteConfirm.title') 
                : t('mediaGallery.bulkDeleteConfirm.title')}
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              {itemToDelete 
                ? t('mediaGallery.deleteConfirm.message')
                : t('mediaGallery.bulkDeleteConfirm.message', { count: selectedItems.length })}
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                className="px-4 py-2 border rounded-md hover:bg-gray-50"
                onClick={() => setShowDeleteConfirmation(false)}
              >
                {t('mediaGallery.deleteConfirm.cancel')}
              </button>
              <button
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                onClick={itemToDelete ? confirmDelete : handleBulkDelete}
              >
                {t('mediaGallery.deleteConfirm.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Upload modal - simplified version */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-lg">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('mediaGallery.upload')}</h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={() => setShowUploadModal(false)}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div 
              className="border-2 border-dashed border-gray-300 rounded-lg p-4 sm:p-8 text-center"
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.add('border-primary-500', 'bg-primary-50');
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
              }}
              onDrop={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                e.currentTarget.classList.remove('border-primary-500', 'bg-primary-50');
                
                if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                  const file = e.dataTransfer.files[0];
                  await handleFileUpload(file);
                }
              }}
            >
              <div className="flex flex-col items-center">
                {isUploading ? (
                  <>
                    <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-primary-600 mb-4"></div>
                    <h4 className="text-base font-medium text-gray-900 mb-1">{t('mediaGallery.uploading')}</h4>
                    <p className="text-sm text-gray-500">{t('mediaGallery.pleaseWait')}</p>
                  </>
                ) : uploadError ? (
                  <>
                    <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-red-100 text-red-500 mb-4">
                      <X className="w-5 h-5 sm:w-6 sm:h-6" />
                    </div>
                    <h4 className="text-base font-medium text-red-500 mb-1">{t('mediaGallery.uploadError')}</h4>
                    <p className="text-sm text-gray-500 mb-4">{uploadError}</p>
                    <button
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
                      onClick={() => setUploadError(null)}
                    >
                      {t('mediaGallery.tryAgain')}
                    </button>
                  </>
                ) : (
                  <>
                    <Image className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mb-4" />
                    <h4 className="text-base font-medium text-gray-900 mb-1">{t('mediaGallery.dropzone.title')}</h4>
                    <p className="text-sm text-gray-500 mb-4">{t('mediaGallery.dropzone.subtitle')}</p>
                    <p className="text-xs text-gray-400">{t('mediaGallery.dropzone.maxSize')}</p>
                    
                    <input 
                      type="file" 
                      id="file-upload" 
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileUpload(e.target.files[0]);
                        }
                      }}
                    />
                    <label htmlFor="file-upload" className="mt-6 inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors cursor-pointer">
                      <Plus className="w-4 h-4 mr-2" />
                      {t('mediaGallery.upload')}
                    </label>
                  </>
                )}
              </div>
            </div>

            {uploadSuccess && (
              <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start">
                <div className="flex-shrink-0 text-green-500 mt-0.5">
                  <Check className="w-5 h-5" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">
                    {t('mediaGallery.uploadSuccess')}
                  </h3>
                  <div className="mt-1 text-sm text-green-700">
                    {t('mediaGallery.fileUploaded')}
                  </div>
                  <div className="mt-3">
                    <button
                      className="text-sm font-medium text-green-600 hover:text-green-500"
                      onClick={() => setShowUploadModal(false)}
                    >
                      {t('mediaGallery.closeUploadModal')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Bulk delete loading overlay */}
      {isBulkDeleting && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md flex flex-col items-center">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-t-2 border-b-2 border-primary-600 mb-4"></div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 text-center">
              {t('mediaGallery.bulkDeleteProgress.title', 'Deleting media files...')}
            </h3>
            <p className="text-sm text-gray-500 text-center">
              {t('mediaGallery.bulkDeleteProgress.message', 'Please wait while your files are being deleted')}
            </p>
          </div>
        </div>
      )}
      
      {/* Bulk delete success notification */}
      {bulkDeleteSuccess && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl p-4 sm:p-6 w-full max-w-md flex flex-col items-center">
            <div className="text-green-600 mb-4">
              <Check className="w-10 h-10 sm:w-12 sm:h-12" />
            </div>
            <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-1 text-center">
              {t('mediaGallery.bulkDeleteSuccess', 'Files successfully deleted')}
            </h3>
            <p className="text-sm text-gray-500 mb-4 text-center">
              {t('mediaGallery.bulkDeleteSuccess.message', 'Your files have been successfully removed from the media gallery')}
            </p>
          </div>
        </div>
      )}
      
      {/* Rename modal */}
      {showRenameModal && itemToRename && (
        <RenameModal
          isOpen={showRenameModal}
          onClose={() => setShowRenameModal(false)}
          onConfirm={confirmRename}
          currentName={itemToRename.currentName}
        />
      )}
    </>
  );
};

export default MediaGallery; 